import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useTranslation } from '../i18n'
import {
  buildPinTypeCatalog,
  catalogLocale,
  type PinTypeCatalog,
  type PinTypeEntry,
} from '../lib/pinTypeCatalog'

const PinTypeCatalogContext = createContext<PinTypeCatalog | null>(null)

/**
 * Hands the pin-type catalog to every component under it. The host (the
 * clubhouse package in the app, the fixture in the prototype and in tests)
 * supplies the parsed entries; this provider resolves the display locale from
 * the i18n language so labels follow the app language everywhere.
 */
export function PinTypeCatalogProvider({
  entries,
  ready,
  children,
}: {
  entries: readonly PinTypeEntry[]
  ready: boolean
  children: ReactNode
}) {
  const { i18n } = useTranslation()
  const locale = catalogLocale(i18n.language)
  const catalog = useMemo(() => buildPinTypeCatalog(entries, locale, ready), [entries, locale, ready])
  return <PinTypeCatalogContext.Provider value={catalog}>{children}</PinTypeCatalogContext.Provider>
}

/**
 * The catalog for the current tree. Throws when mounted outside a
 * PinTypeCatalogProvider: that is a mounting bug in the host, never a state
 * to recover from with a silent default (see the mobile rulebook's rule on
 * InheritedWidget `.of()`). Tests mount the provider through renderWithI18n.
 */
export function usePinTypeCatalog(): PinTypeCatalog {
  const catalog = useContext(PinTypeCatalogContext)
  if (!catalog) {
    throw new Error('usePinTypeCatalog: no PinTypeCatalogProvider above this component')
  }
  return catalog
}
